import { useState, type FormEvent } from 'react';
import { useAuth } from '@/app/AuthContext';
import {
  useCreateAdjustment,
  useCreateComment,
  useValueDetail,
} from '@/app/hooks';
import { Badge } from './Badge';
import { Button } from './Button';
import { Icon } from './Icon';
import { Spinner } from './LoadingScreen';
import { ApiRequestError } from '@/services/api';
import { Permission } from '@/types/permission';
import { formatAmd, toNumber } from '@/utils/format';
import { font, v } from '@/theme/tokens';

interface Props {
  unitId: string;
  categoryId: string;
  categoryName: string;
  periodId: string;
  periodClosed: boolean;
  onClose: () => void;
}

/** Drill-down for one P&L number: source, versions, adjustments, comments. */
export function ValueDrawer({ unitId, categoryId, categoryName, periodId, periodClosed, onClose }: Props) {
  const { can } = useAuth();
  const detail = useValueDetail(unitId, categoryId, periodId);
  const createAdjustment = useCreateAdjustment();
  const createComment = useCreateComment();

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustValue, setAdjustValue] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const data = detail.data;
  const canAdjust = can(Permission.CREATE_MANUAL_ADJUSTMENT) && !periodClosed;
  const canComment = can(Permission.COMMENT);

  const submitAdjustment = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      await createAdjustment.mutateAsync({
        business_unit_id: unitId,
        financial_period_id: periodId,
        financial_category_id: categoryId,
        adjustment_value: adjustValue.replace(',', '.').replace(/\s/g, ''),
        reason: adjustReason,
      });
      setAdjustOpen(false);
      setAdjustValue('');
      setAdjustReason('');
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'Не удалось сохранить корректировку');
    }
  };

  const submitComment = async (event: FormEvent) => {
    event.preventDefault();
    if (!commentBody.trim()) return;
    setError(null);
    try {
      await createComment.mutateAsync({
        body: commentBody,
        business_unit_id: unitId,
        financial_period_id: periodId,
        financial_category_id: categoryId,
      });
      setCommentBody('');
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'Не удалось сохранить комментарий');
    }
  };

  const label = (text: string) => (
    <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' as const, color: v('ink-muted'), margin: '18px 0 8px' }}>
      {text}
    </div>
  );
  const fieldStyle = {
    width: '100%',
    padding: '9px 11px',
    border: `1px solid ${v('line')}`,
    borderRadius: 9,
    background: v('surface'),
    color: v('ink'),
    fontSize: 13.5,
    fontFamily: 'inherit',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', justifyContent: 'flex-end' }}>
      <div
        onClick={onClose}
        className="rt-scrim"
        style={{ position: 'absolute', inset: 0 }}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-label={categoryName}
        className="rt-drawer"
        style={{
          position: 'relative',
          width: 460,
          maxWidth: '94vw',
          height: '100%',
          overflowY: 'auto',
          padding: '20px 22px 40px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{categoryName}</div>
          <button
            onClick={onClose}
            aria-label="Закрыть"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, color: v('ink-muted') }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        {detail.isPending && <Spinner />}
        {detail.error && (
          <div style={{ color: v('negative'), fontSize: 13 }}>
            {detail.error instanceof Error ? detail.error.message : 'Ошибка загрузки'}
          </div>
        )}

        {data && (
          <>
            <div style={{ fontSize: 12.5, color: v('ink-muted') }}>{data.period_label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, fontFamily: font.mono, margin: '10px 0 4px' }}>
              {formatAmd(toNumber(data.amount))}
            </div>
            {data.source_type && (
              <Badge tone={data.source_type === 'MANUAL_ADJUSTMENT' ? 'warning' : 'neutral'}>
                {data.source_type === 'MANUAL_ADJUSTMENT' ? 'С корректировкой' : 'Импорт'}
              </Badge>
            )}

            {data.source && (
              <>
                {label('Источник данных')}
                <div style={{ background: v('panel'), borderRadius: 10, padding: 14, fontSize: 13, lineHeight: 1.7, color: v('ink-sub') }}>
                  Файл: <b style={{ color: v('ink') }}>{data.source.filename}</b>
                  <br />Ревизия: {data.source.revision}
                  {data.source.scope_label ? ` · ${data.source.scope_label}` : ''}
                  <br />Загрузил: {data.source.uploaded_by?.full_name ?? '—'}
                  <br />
                  {new Date(data.source.uploaded_at).toLocaleString('ru-RU')}
                </div>
              </>
            )}

            {label('Версии значения')}
            {data.history.map((entry) => (
              <div
                key={entry.version}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: `1px solid ${v('line-soft')}`, fontSize: 13 }}
              >
                <span style={{ fontFamily: font.mono, color: v('ink-muted'), width: 28 }}>v{entry.version}</span>
                <span style={{ flex: 1, fontFamily: font.mono, fontWeight: entry.is_current ? 700 : 400, color: entry.is_current ? v('ink') : v('ink-muted') }}>
                  {formatAmd(toNumber(entry.amount))}
                </span>
                <Badge tone={entry.is_current ? 'positive' : 'neutral'}>
                  {entry.source_type === 'MANUAL_ADJUSTMENT' ? 'корректировка' : `импорт${entry.import_revision ? ` r${entry.import_revision}` : ''}`}
                </Badge>
              </div>
            ))}

            {label('Корректировки')}
            {data.adjustments.length === 0 && (
              <div style={{ fontSize: 13, color: v('ink-muted') }}>Корректировок нет.</div>
            )}
            {data.adjustments.map((adj) => (
              <div key={adj.id} style={{ background: v('panel'), borderRadius: 10, padding: 12, marginBottom: 8, fontSize: 13 }}>
                <div style={{ display: 'flex', gap: 8, fontFamily: font.mono, fontWeight: 600 }}>
                  <span style={{ color: v('ink-muted') }}>{formatAmd(toNumber(adj.base_value))}</span>
                  <span>→</span>
                  <span>{formatAmd(toNumber(adj.result_value))}</span>
                </div>
                <div style={{ color: v('ink-sub'), marginTop: 6 }}>{adj.reason}</div>
                <div style={{ color: v('ink-muted'), fontSize: 12, marginTop: 4 }}>
                  {adj.author?.full_name ?? '—'} · {new Date(adj.created_at).toLocaleString('ru-RU')}
                </div>
              </div>
            ))}

            {canAdjust && !adjustOpen && (
              <Button onClick={() => setAdjustOpen(true)}>Добавить корректировку</Button>
            )}
            {periodClosed && (
              <div style={{ fontSize: 12.5, color: v('ink-muted'), marginTop: 6 }}>
                Период закрыт — корректировки недоступны.
              </div>
            )}
            {adjustOpen && (
              <form onSubmit={submitAdjustment} style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ fontSize: 12.5, fontWeight: 600, color: v('ink-sub') }} htmlFor="adj-value">
                  Сумма корректировки (± ֏)
                </label>
                <input
                  id="adj-value" required value={adjustValue}
                  onChange={(e) => setAdjustValue(e.target.value)}
                  placeholder="+30000" inputMode="decimal" style={fieldStyle}
                />
                <label style={{ fontSize: 12.5, fontWeight: 600, color: v('ink-sub') }} htmlFor="adj-reason">
                  Причина (обязательно)
                </label>
                <textarea
                  id="adj-reason" required minLength={3} value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  rows={2} style={{ ...fieldStyle, resize: 'vertical' as const }}
                  placeholder="Например: счёт за обслуживание кондиционеров"
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button type="submit" variant="primary" disabled={createAdjustment.isPending}>
                    Сохранить
                  </Button>
                  <Button type="button" onClick={() => setAdjustOpen(false)}>Отмена</Button>
                </div>
              </form>
            )}

            {label('Комментарии')}
            {data.comments.length === 0 && (
              <div style={{ fontSize: 13, color: v('ink-muted') }}>Комментариев нет.</div>
            )}
            {data.comments.map((comment) => (
              <div key={comment.id} style={{ padding: '8px 0', borderBottom: `1px solid ${v('line-soft')}`, fontSize: 13 }}>
                <div style={{ color: v('ink') }}>{comment.body}</div>
                <div style={{ color: v('ink-muted'), fontSize: 12, marginTop: 3 }}>
                  {comment.author?.full_name ?? '—'} · {new Date(comment.created_at).toLocaleString('ru-RU')}
                </div>
              </div>
            ))}
            {canComment && (
              <form onSubmit={submitComment} style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <input
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value)}
                  placeholder="Короткое пояснение…"
                  aria-label="Новый комментарий"
                  style={{ ...fieldStyle, flex: 1 }}
                />
                <Button type="submit" variant="primary" disabled={createComment.isPending || !commentBody.trim()}>
                  Добавить
                </Button>
              </form>
            )}

            {error && <div style={{ color: v('negative'), fontSize: 13, marginTop: 10 }} role="alert">{error}</div>}
          </>
        )}
      </aside>
    </div>
  );
}
